import type { ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Group, Paper, Text } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconGripVertical } from '@tabler/icons-react';

interface SortableListProps<T> {
  items: T[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  onChange: (items: T[]) => void;
  renderItem: (item: T, index: number) => ReactNode;
  disabled?: boolean;
  /** Accessible name of the list, e.g. "Collection items". */
  label: string;
}

/**
 * A vertically sortable list: drag by the handle (mouse, touch or keyboard: focus the handle,
 * Space to lift, arrows to move, Space to drop), or use the move up/down buttons.
 */
export function SortableList<T>({
  items,
  getId,
  getLabel,
  onChange,
  renderItem,
  disabled,
  label,
}: SortableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(getId);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(arrayMove(items, from, to));
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    onChange(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up ${labelFor(active.id)}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `${labelFor(active.id)} is now at position ${ids.indexOf(String(over.id)) + 1} of ${ids.length}.`
              : '',
          onDragEnd: ({ active, over }) =>
            over
              ? `Dropped ${labelFor(active.id)} at position ${ids.indexOf(String(over.id)) + 1}.`
              : `Dropped ${labelFor(active.id)}.`,
          onDragCancel: ({ active }) => `Cancelled moving ${labelFor(active.id)}.`,
        },
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy} disabled={disabled}>
        <ol
          aria-label={label}
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {items.map((item, i) => (
            <SortableRow
              key={getId(item)}
              id={getId(item)}
              index={i}
              count={items.length}
              label={getLabel(item)}
              disabled={disabled}
              onMove={move}
            >
              {renderItem(item, i)}
            </SortableRow>
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );

  function labelFor(id: string | number) {
    const item = items.find((x) => getId(x) === String(id));
    return item ? getLabel(item) : 'item';
  }
}

function SortableRow({
  id,
  index,
  count,
  label,
  disabled,
  onMove,
  children,
}: {
  id: string;
  index: number;
  count: number;
  label: string;
  disabled?: boolean;
  onMove: (from: number, to: number) => void;
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <li
      ref={setNodeRef}
      className="pb-sortable-row"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
        position: 'relative',
      }}
      data-testid="sortable-row"
    >
      <Paper
        withBorder
        p={6}
        pl={4}
        radius="sm"
        shadow={isDragging ? 'md' : undefined}
        className="pb-surface"
      >
        <Group gap="xs" wrap="nowrap">
          {!disabled && (
            <ActionIcon
              ref={setActivatorNodeRef}
              variant="subtle"
              color="gray"
              className="pb-drag-handle"
              aria-label={`Drag to reorder ${label}`}
              {...attributes}
              {...listeners}
            >
              <IconGripVertical size={16} />
            </ActionIcon>
          )}
          <Text size="xs" c="dimmed" w={20} ta="right" aria-hidden>
            {index + 1}
          </Text>
          <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
          {!disabled && (
            <Group gap={2} wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Move ${label} up`}
                disabled={index === 0}
                onClick={() => onMove(index, index - 1)}
              >
                <IconArrowUp size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Move ${label} down`}
                disabled={index === count - 1}
                onClick={() => onMove(index, index + 1)}
              >
                <IconArrowDown size={16} />
              </ActionIcon>
            </Group>
          )}
        </Group>
      </Paper>
    </li>
  );
}
